import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useLocation, useSearchParams, Navigate } from 'react-router-dom';


// Redux.
import { connect } from 'react-redux';
import { signinUser } from '../redux/actions/userActions';

// Router.
import { useNavigate } from "react-router-dom";

// Firebase.
import { getAnalytics, logEvent } from "firebase/analytics";
import { auth, isAnonymousFirebaseUser } from '../firebaseHelper'

import AuthFlowLayout from '../components/AuthFlowLayout';
import PhoneSigninPanel from '../components/PhoneSigninPanel';
import { getSigninRedirect } from '../components/signinUtils';
import { readOnboardingFlow, trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';

const Signin = ({ signinUser, authenticated }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const audio = document.getElementById('audio');
    const [phoneStage, setPhoneStage] = useState('phone');
    const [phoneBusy, setPhoneBusy] = useState(false);
    const phonePanelRef = useRef(null);
    const signupOpenLoggedRef = useRef(false);

    const [searchParams] = useSearchParams();
    const requestedRedirect = searchParams.get('redirect');
    const redirect = useMemo(() => getSigninRedirect(requestedRedirect, location.state), [requestedRedirect, location.state]);
    const cancelRedirect = location.state?.signinCancelToHome
        ? { path: '/home', replace: true }
        : redirect;

    useEffect(() => {
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'signin'
        });
        audio?.pause();
        if (!signupOpenLoggedRef.current) {
            signupOpenLoggedRef.current = true;
            trackOnboardingEvent('onboarding_signup_open', { signup_trigger: 'other' });
        }
    }, [audio]);

    // Listen to the Firebase Auth state and set redux state.
    useEffect(() => {
        let isActive = true;
        const unregisterAuthObserver = auth.onAuthStateChanged(async (user) => {
            if (!!user && !isAnonymousFirebaseUser(user)) {
                const flowId = readOnboardingFlow()?.flowId || null;
                try {
                    const idToken = await user.getIdToken();
                    const signinResult = await signinUser(idToken, user.refreshToken, {});
                    if (!signinResult?.success) throw signinResult?.error || new Error('Account setup failed');
                    trackOnboardingEvent('onboarding_ready', {}, { flowId });
                    if (!isActive) return;

                    const analytics = getAnalytics();
                    logEvent(analytics, 'signin');
                    navigate(redirect.path, { state: redirect.state, replace: redirect.replace });
                } catch (error) { trackOnboardingError('bootstrap', error, { flowId }); }
            }
        });
        return () => {
            isActive = false;
            unregisterAuthObserver();
        }; // Make sure we un-register Firebase observers when the component unmounts.
    }, [navigate, redirect, signinUser]);

    if (!authenticated) {
        return (
            <AuthFlowLayout
                title={phoneStage === 'code' ? 'Enter your code' : 'Sign in'}
                onBack={phoneBusy ? undefined : phoneStage === 'code'
                    ? () => phonePanelRef.current?.changeNumber()
                    : () => navigate(cancelRedirect.path, { state: cancelRedirect.state, replace: cancelRedirect.replace })}
                backLabel={phoneStage === 'code' ? 'Change phone number' : 'Back'}
                className='signinPage'
            >
                <PhoneSigninPanel ref={phonePanelRef} className='authFlowPhonePanel' onStageChange={setPhoneStage} onBusyChange={setPhoneBusy} hasBackAction />
            </AuthFlowLayout>
        );
    } else {
        return <Navigate to={redirect.path} state={redirect.state} replace={redirect.replace} />
    }
}

Signin.propTypes = {
    signinUser: PropTypes.func.isRequired,
    authenticated: PropTypes.bool.isRequired
}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated
})

const mapActionsToProps = {
    signinUser
}

export default connect(mapStateToProps, mapActionsToProps)(Signin);
